import { useEffect, useRef } from 'react';
import { useApi, storageApiRef } from '@backstage/core-plugin-api';
import { starredEntitiesApiRef } from '@backstage/plugin-catalog-react';

/**
 * Entity refs that should arrive **already starred** for every user the
 * first time they hit the portal in a given browser.
 *
 * Today: both language variants of the Onboarding Guide. The catalog
 * "Starred" filter and the docs sidebar (`/docs`) read from the same
 * `starredEntitiesApi` bucket, so seeding here surfaces the guide in
 * BOTH places without any docs-specific wiring.
 *
 * To add another default favourite, just push the entityRef here. Refs
 * follow `<kind>:<namespace>/<name>`; `default` is the namespace every
 * entity in this portal lives in (we don't use namespaces).
 */
const DEFAULT_STARRED_ENTITIES: readonly string[] = [
  'component:default/onboarding-guide',
  'component:default/onboarding-guide-pt-br',
];

/**
 * Where we persist the "this browser has already been seeded" flag.
 *
 * Bucketed separately from `starredEntities` (the bucket the catalog
 * plugin's `DefaultStarredEntitiesApi` writes to — see
 * `node_modules/@backstage/plugin-catalog/dist/apis/StarredEntitiesApi/
 * DefaultStarredEntitiesApi.esm.js`) so we can evolve the seed
 * mechanism without colliding with upstream keys.
 *
 * The `.v1` suffix on the key is intentional: when we want to ship a
 * new wave of default favourites in the future, bumping to `.v2` lets
 * us re-seed every user once without losing their accumulated stars
 * (the seeder only ADDS — it never removes anything from the user's
 * starred set, see the `currentSet.has(ref)` filter below).
 */
const SEED_BUCKET = 'bosch.df.app-seeds';
const SEED_KEY = 'starred-defaults.v1';

/**
 * Always-mounted side-effect component. Renders `null` — its only job
 * is to add `DEFAULT_STARRED_ENTITIES` to the user's starred set the
 * first time they land in the portal, so the Onboarding Guide shows
 * up as a starred item in `/catalog` (and in `/docs`, which reads
 * from the same starred API).
 *
 * Mounted at the React root via `AppRootElementBlueprint` in
 * `packages/app/src/modules/seeds/index.tsx`. That blueprint wraps
 * the component in an `ExtensionBoundary` with `errorPresentation:
 * "error-api"`, so any unexpected throw shows up as a toast instead
 * of breaking the app.
 *
 * Why a one-shot seeder instead of just hard-coding the stars in the
 * catalog plugin's storage:
 *
 *   - **Per-user respect.** If the user later un-stars the
 *     onboarding guide, the SEED_KEY flag is already set so we never
 *     re-add it. We seed at most once per browser, ever (modulo bumping
 *     the version suffix).
 *
 *   - **No backend required.** The starred state lives in the storage
 *     API (`WebStorage` by default = `localStorage`). Seeding from
 *     the frontend is a single side effect — no backend migration, no
 *     database touch, no extra plugin to install.
 *
 *   - **Survives logout/login (per browser).** The flag is in the
 *     same `localStorage` as the stars themselves, so re-signing in
 *     doesn't clear it.
 *
 *   - **Granular reset.** Power users can clear `localStorage` in
 *     their devtools to "factory reset" the seeds and get them back.
 */
export const DefaultFavoritesSeeder = () => {
  const starredApi = useApi(starredEntitiesApiRef);
  const storage = useApi(storageApiRef);

  /*
   * Re-entry guard: each `toggleStarred` call notifies subscribers,
   * which fires our `next` callback again. Without this ref we'd
   * recompute `missing` on every notification and could double-toggle
   * an already-seeded entity (which would un-star it). Setting the
   * ref BEFORE any toggleStarred call short-circuits subsequent fires
   * for the lifetime of this component instance.
   */
  const inFlightRef = useRef(false);

  useEffect(() => {
    const seedBucket = storage.forBucket(SEED_BUCKET);
    const persisted = seedBucket.snapshot<boolean>(SEED_KEY).value;
    if (persisted || inFlightRef.current) {
      return undefined;
    }

    /*
     * `starredEntitie$()` (the typo is upstream — see
     * `DefaultStarredEntitiesApi.esm.js`, method literally named
     * `starredEntitie$`) is a hot zen-observable that fires
     * synchronously with the current Set on subscribe and again on
     * every change. We use the synchronous first emission to read the
     * current stars without an extra round-trip.
     */
    const subscription = starredApi.starredEntitie$().subscribe({
      next: (currentSet: Set<string>) => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;

        const missing = DEFAULT_STARRED_ENTITIES.filter(
          ref => !currentSet.has(ref),
        );

        for (const ref of missing) {
          /*
           * Fire-and-forget. `toggleStarred` is async (it awaits a
           * `storage.set` write internally) but the in-memory Set on
           * the API is updated synchronously inside that method
           * BEFORE the await — so subsequent reads from other
           * components see the new state immediately, regardless of
           * the storage write completing.
           *
           * `.catch` swallows storage failures (private-browsing
           * mode, disabled cookies, quota exhausted) — we don't want
           * a one-time UX nicety to surface as an error toast.
           */
          starredApi.toggleStarred(ref).catch(() => {});
        }

        seedBucket.set(SEED_KEY, true).catch(() => {});
        subscription.unsubscribe();
      },
    });

    return () => subscription.unsubscribe();
  }, [starredApi, storage]);

  return null;
};
